import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@peacecover/component';
import { ArrowLeft } from '@peacecover/icon';
import { useCheckDuplicateSnsAccount } from '@pocapan/shared/domains/findBuddy/ui/hooks/useCheckDuplicateSnsAccount';
import { useSuccessPhotoCardToast } from '../../template/edit-collected-photo-cards/hooks/useSuccessPhotoCardToast';
import SnsInputBox from './SnsInputBox';
import { ReactComponent as GradientAttention } from '../../../icons/gradient.attention.svg';
import type { FanBuddySettingStep, SnsAccount } from '../FanBuddySettingPage';

interface RegisterSnsAccountStepProps {
  handleSettingStep: (step: FanBuddySettingStep) => void;
  snsAccounts: SnsAccount;
  onUpdateSnsAccounts: (snsAccounts: SnsAccount) => void;
  onAddBuddyPreference: (snsAccountsOverride?: SnsAccount) => void;
  onBackToBuddyKeywordsAllSteps?: () => void;
}

const RegisterSnsAccountStep = ({
  handleSettingStep,
  snsAccounts,
  onUpdateSnsAccounts,
  onAddBuddyPreference,
  onBackToBuddyKeywordsAllSteps
}: RegisterSnsAccountStepProps) => {
  const { t } = useTranslation();
  const { mutateAsync: checkDuplicateSnsAccount } = useCheckDuplicateSnsAccount();
  const showSuccessToast = useSuccessPhotoCardToast();
  const [isAnyInputFocused, setIsAnyInputFocused] = useState(false);

  const handleCheckDuplicateSnsAccount = async (
    snsType: 'twitter' | 'instagram',
    account: string
  ) => {
    const response = await checkDuplicateSnsAccount({
      [snsType === 'instagram' ? 'instagram_id' : 'twitter_id']: account
    });
    if (response) {
      onUpdateSnsAccounts({ ...snsAccounts, [snsType]: { id: account, isCheckDuplicate: true } });
      showSuccessToast('bottom', t('FanBuddySetting.successDuplicateCheck') as string);
    } else {
      showSuccessToast(
        'bottom',
        t('FanBuddySetting.failedDuplicateCheck') as string,
        false,
        <GradientAttention width={20} height={20} />
      );
    }
  };

  const handleComplete = () => {
    onAddBuddyPreference(snsAccounts);
    handleSettingStep('loading-profile-card');
  };

  const handleSkip = () => {
    const clearedAccounts: SnsAccount = {
      twitter: { id: '', isCheckDuplicate: false },
      instagram: { id: '', isCheckDuplicate: false }
    };
    onUpdateSnsAccounts(clearedAccounts);
    onAddBuddyPreference(clearedAccounts);
    handleSettingStep('loading-profile-card');
  };

  const isDisabled =
    (snsAccounts.twitter.id && !snsAccounts.twitter.isCheckDuplicate) ||
    (snsAccounts.instagram.id && !snsAccounts.instagram.isCheckDuplicate);

  return (
    <div
      className="min-h-screen pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
      onFocusCapture={() => setIsAnyInputFocused(true)}
      onBlurCapture={(e) => {
        const nextFocused = e.relatedTarget as Node | null;
        if (!nextFocused || !e.currentTarget.contains(nextFocused)) {
          setIsAnyInputFocused(false);
        }
      }}
    >
      <Header
        onBack={onBackToBuddyKeywordsAllSteps ?? (() => handleSettingStep('select-buddy-keywords'))}
        onSkip={handleSkip}
      />
      <div className="px-[24px]">
        <h1 className="text-[26px] font-bold leading-[31px] text-text-main-lv1 my-[10px] whitespace-pre-line">
          {t('FanBuddySetting.showSnsAccounts')}
        </h1>
        <SnsInputBox
          snsType="instagram"
          account={snsAccounts.instagram.id ?? ''}
          isCheckDuplicate={snsAccounts.instagram.isCheckDuplicate}
          onChange={(e) =>
            onUpdateSnsAccounts({
              ...snsAccounts,
              instagram: { id: e.target.value, isCheckDuplicate: false }
            })
          }
          onCheckDuplicate={() =>
            handleCheckDuplicateSnsAccount('instagram', snsAccounts.instagram.id ?? '')
          }
          className="mt-[38px]"
        />
        <SnsInputBox
          snsType="twitter"
          account={snsAccounts.twitter.id ?? ''}
          isCheckDuplicate={snsAccounts.twitter.isCheckDuplicate}
          onChange={(e) =>
            onUpdateSnsAccounts({
              ...snsAccounts,
              twitter: { id: e.target.value, isCheckDuplicate: false }
            })
          }
          onCheckDuplicate={() =>
            handleCheckDuplicateSnsAccount('twitter', snsAccounts.twitter.id ?? '')
          }
          className="mt-[43px]"
        />
      </div>
      <div className="fixed bottom-[calc(10px+env(safe-area-inset-bottom))] left-0 right-0 px-[24px] w-full">
        {!isAnyInputFocused && (
          <p className="text-[13px] font-medium leading-[16px] text-text-main-lv4 mb-[24px] text-center">
            {t('FanBuddySetting.clickDuplicateCheck')}
          </p>
        )}
        <Button
          size="large"
          className="w-full text-[17px] font-semibold"
          onClick={handleComplete}
          disabled={isDisabled}
        >
          {t('confirm')}
        </Button>
      </div>
    </div>
  );
};

export default RegisterSnsAccountStep;

const Header = ({ onBack, onSkip }: { onBack: () => void; onSkip: () => void }) => {
  const { t } = useTranslation();
  return (
    <header className="px-[16px] py-[12px] flex items-center justify-between w-full relative text-text-main-lv1">
      <button type="button" onClick={onBack}>
        <ArrowLeft size={24} />
      </button>
      <p className="text-[17px] font-bold leading-[20px] absolute left-1/2 transform -translate-x-1/2">
        {t('FanBuddySetting.title')}
      </p>
      <button type="button" className="text-[16px] font-medium leading-[19px]" onClick={onSkip}>
        {t('FanBuddySetting.skip')}
      </button>
    </header>
  );
};
